import { ErrorHandler, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { Router, RouterModule, UrlSerializer } from '@angular/router';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';

import { OAuthModule } from 'angular-oauth2-oidc';
import { InViewportModule } from 'ng-in-viewport';
import { ToastrModule } from 'ngx-toastr';
import { DndModule } from 'ngx-drag-drop';
import { GoogleMapsModule } from '@angular/google-maps';
import { EmblaCarouselDirective } from 'embla-carousel-angular';
import * as Sentry from '@sentry/angular';

import {
	provideUnleashProxy,
	FeatureEnabledDirective,
	FeatureDisabledDirective,
	featureEnabled,
} from '@karelics/angular-unleash-proxy-client';
import { provideDialogConfig } from '@ngneat/dialog';

import { popperVariation, provideTippyConfig, provideTippyLoader } from '@ngneat/helipopper/config';
import { withContextMenuVariation } from '@ngneat/helipopper/config';

import { environment } from '../environments/environment';

import { AppComponent } from './app.component';
import { BannerComponent } from './features/shared/components/banner/banner.component';

import { AnonymousGuard } from './guards/anonymous.guard';
import { AuthorizedGuard } from './guards/authorized.guard';
import { TenantGuard } from './guards/tenant.guard';

import { AuthInterceptor } from './interceptors/auth.interceptor';
import { ErrorInterceptor } from './interceptors/error.interceptor';

import { CustomUrlSerializer } from './features/shared/serializers/url.serializer';
import { SessionStore } from './store/session.store';

import { AUTH_ROUTES } from './features/auth/auth.routes';
import { CAMPAIGN_ROUTES } from './features/campaigns/campaigns.routes';
import { INSIGHTS_ROUTES } from './features/insights/insights.routes';
import { SETTINGS_ROUTES } from './features/settings/settings.routes';
import { featureGuard } from './guards/feature.guard';

@NgModule({
	declarations: [AppComponent],
	bootstrap: [AppComponent],
	imports: [
		DndModule,
		EmblaCarouselDirective,
		FeatureEnabledDirective,
		FeatureDisabledDirective,
		RouterModule.forRoot([
			{
				path: 'auth',
				loadComponent: () => import('./features/auth/auth.component').then((m) => m.AuthComponent),
				canActivate: [AnonymousGuard],
				children: AUTH_ROUTES,
			},
			{
				path: ':tenant',
				loadComponent: () => import('./features/container/container.component').then((m) => m.ContainerComponent),
				canActivate: [AuthorizedGuard, TenantGuard],
				children: [
					{
						path: 'studio',
						canActivate: [featureEnabled('genai-content-search', '/dashboard')],
						loadComponent: () =>
							import('./features/content/search/search.component').then((m) => m.ContentSearchComponent),
					},
					{
						path: 'dashboard',
						loadComponent: () => import('./features/dashboard/dashboard.component').then((m) => m.DashboardComponent),
					},
					{
						path: 'content',
						loadComponent: () =>
							import('./features/content-drilldown/content-drilldown.component').then(
								(m) => m.AppleContentDrilldownComponent
							),
					},
					{
						path: 'touchpoints',
						loadComponent: () =>
							import('./features/touchpoint-drilldown/touchpoint-drilldown.component').then(
								(m) => m.TouchpointDrilldownComponent
							),
					},
					{
						path: 'campaigns',
						children: CAMPAIGN_ROUTES,
						data: {
							feature: 'campaigns',
						},
					},
					{
						path: 'insights',
						loadComponent: () => import('./features/insights/insights.component').then((m) => m.InsightsComponent),
						children: INSIGHTS_ROUTES,
						canActivateChild: [featureGuard],
						data: {
							feature: 'insights',
						},
					},
					{
						path: 'settings',
						children: SETTINGS_ROUTES,
						canActivateChild: [featureGuard],
					},
				],
			},
			{
				path: ':tenant/content/approval',
				loadComponent: () => import('./features/container/container.component').then((m) => m.ContainerComponent),
				canActivate: [AuthorizedGuard],
				data: {
					'background-color': 'var(--white)',
					padding: '0px 25px 0px 25px',
				},
				children: [
					{
						path: ':id',
						loadComponent: () => import('./features/approvals/approvals.component').then((m) => m.ApprovalsComponent),
					},
				],
			},
			{
				path: '**',
				redirectTo: '/auth/login',
			},
		]),
		BrowserModule,
		InViewportModule,
		BrowserAnimationsModule,
		FormsModule,
		ReactiveFormsModule,
		CommonModule,
		GoogleMapsModule,
		BrowserAnimationsModule,
		ToastrModule.forRoot({
			toastComponent: BannerComponent,
			closeButton: true,
			maxOpened: 1,
			preventDuplicates: true,
		}),
		OAuthModule.forRoot(),
	],
	providers: [
		SessionStore,
		{ provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
		{
			provide: ErrorHandler,
			useValue: Sentry.createErrorHandler({
				showDialog: false,
			}),
		},
		{
			provide: Sentry.TraceService,
			deps: [Router],
		},
		provideUnleashProxy({
			url: environment.unleash.endpoint,
			clientKey: environment.unleash.clientKey,
			appName: environment.unleash.appName,
			environment: environment.unleash.envrionment,
		}),
		provideHttpClient(withInterceptorsFromDi()),
		provideDialogConfig({
			closeButton: false,
		}),
		{ provide: UrlSerializer, useClass: CustomUrlSerializer },
		provideTippyLoader(() => import('tippy.js')),
		provideTippyConfig({
			defaultVariation: 'tooltip',
			variations: {
				tooltip: {
					theme: 'light',
					arrow: true,
					animation: 'scale',
					trigger: 'mouseenter',
					offset: [0, 12],
				},
				context: {
					theme: 'light',
					arrow: false,
					offset: [-8, 3],
					animation: undefined,
					trigger: 'click',
					interactive: true,
				},
				reference: {
					theme: 'light',
					arrow: false,
					animation: 'scale',
					trigger: 'mouseenter',
					offset: [0, 8],
				},
				autocomplete: {
					theme: 'light',
					arrow: false,
					offset: [2, 5],
					animation: undefined,
					trigger: 'manual',
					interactive: true,
				},
				popper: popperVariation,
				contextMenu: {
					...withContextMenuVariation(popperVariation),
					offset: [4, -8],
				},
			},
		}),
	],
})
export class AppModule {}
